import { Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef, OnDestroy, AfterViewChecked, ViewChildren, QueryList } from '@angular/core';
import { LandingPageTile } from '../../shared/tiles/home-tile.model';
import { _ } from '../../shared/utils';
import { AppMessageService } from '../../shared/app-message/app-message.service';
import { SidebarService } from '../../shared/sidebar/sidebar.service';
import { SidebarPanelComponent } from '../../sidebar/sidebar-panel/sidebar-panel.component';
import { VivoService } from '../vivo.service';
import { VivoBody } from '../../sidebar/vivo-filters/vivo-filters.model';
import { Subject, BehaviorSubject } from 'rxjs';
import { takeUntil, map, debounceTime } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { range, remove } from 'lodash-es';
import { parseJSON, isSameDay } from 'date-fns';
import { MapService, MapLatLng } from '~app/shared/map/map.service';
import { FlagService } from '~app/shared/flag/flag.service';
import { AuthService } from '~app/auth/auth.service';
import { IFlag } from '~app/shared/flag/flag.model';
import { StateService } from '~app/shared/state/state.service';
import { PushNotificationsService } from '~app/shared/app-message/push-notifications.service';

interface TaskLocation {
  _id: string;
  label: string;
  link?: string;
  coordinates?: MapLatLng;
}

@Component({
  selector: 'qm-vivo-list',
  templateUrl: './vivo-list.component.html',
  styleUrls: ['./vivo-list.component.scss']
})
export class VivoListComponent extends SidebarPanelComponent implements OnInit, OnDestroy, AfterViewChecked {
  // \xa0 == &nbsp;
  public readonly dateFormat: string = 'HH:mm\xa0\xa0dd.MM.yyyy';

  public events: any[] = [];
  public comments: Map<string, BehaviorSubject<any[]>> = new Map();
  public flags: Map<string, IFlag> = new Map();
  public activeId: string;
  public commentText: string = null;
  public edit: boolean = false;

  public selectedEdit: any;

  public vivoBody: VivoBody; // initialized in ngOnInit
  public pagination: any;

  // used to scroll the list to the top
  @ViewChild('eventList')
  private eventList: ElementRef<HTMLDivElement>;
  private previousFirstEventId: string | undefined = undefined;
  @ViewChildren('commentInputs')
  private commentInputs: QueryList<ElementRef<HTMLTextAreaElement>>;

  private ngUnsubscribe: Subject<void> = new Subject();

  constructor(private vivoService: VivoService,
              private translateService: TranslateService,
              private mapService: MapService,
              private flagService: FlagService,
              private authService: AuthService,
              private stateService: StateService,
              private changeDetector: ChangeDetectorRef,
              private push: PushNotificationsService,
              protected sidebarService: SidebarService,
              protected  appMessageService: AppMessageService) {
    super(appMessageService, sidebarService);
    this.componentName = VivoListComponent.getComponentName();
  }

  public static initTile(tile: LandingPageTile) {
    tile.description = _('Diary');
    tile.icon = 'fas fa-book';
    tile.title = _('Diary');
    tile.panel = VivoListComponent.getComponentName();
  }

  public static getComponentName(): string {
    return 'Diary';
  }

  ngOnInit() {
    this.loading = true;

    this.vivoBody = this.vivoService.getDiaryRequestBody();

    this.vivoService.diaryChanged$.pipe(
      takeUntil(this.ngUnsubscribe),
      debounceTime(1000)
    ).subscribe(() => {
      this.prepareDiary();
    });

    // flags$ is a BehaviorSubject, so triggers the initial loading immediately
    this.flagService.flags$.pipe(
      takeUntil(this.ngUnsubscribe),
      map(flags => flags.filter(flag => flag.targetObjectType === 'diary')),
      debounceTime(100)
    ).subscribe(flags => {
      this.flags = new Map(flags.map(flag => [flag.targetObjectId, flag]));
      this.prepareDiary();
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  // scroll list to top when it changes
  ngAfterViewChecked() {
    // don't scroll if a comment field is focused
    if (this.commentInputs.some(ref => document.activeElement === ref.nativeElement)) {
      return;
    }

    const firstId = this.events[0]?._id;
    if (firstId !== this.previousFirstEventId) {
      this.eventList?.nativeElement.scrollTo({ top: 0 });
      this.previousFirstEventId = firstId;
    }
  }

  private async prepareDiary(): Promise<void> {
    // store any flagged events that are currently visible to prevent flickering during update
    let oldFlaggedEvents = [];
    if (this.pagination?.page === 1) {
      oldFlaggedEvents = this.events.filter(event => this.flags.has(event._id));
    }

    this.pagination = await this.vivoService.getDiaryEvents(this.vivoBody);
    this.events = this.pagination.docs || [];

    // pull out flagged entries; they'll be added to the first page
    const flaggedEvents = remove(this.events, event => this.flags.has(event._id));

    // load flagged events that weren't on the first page
    if (this.pagination?.page === 1) {
      const missingIds = Array.from(this.flags.keys()).filter(id => !flaggedEvents.some(event => event._id === id));
      if (missingIds.length > 0) {
        // temporarily add old flagged events to prevent flickering while requests are pending
        this.events.unshift(...oldFlaggedEvents);
        this.changeDetector.markForCheck();

        for (const result of await Promise.allSettled(missingIds.map(id => this.vivoService.getTask(id)))) {
          if (result.status === 'fulfilled') {
            flaggedEvents.push(result.value);
          }
        }

        // remove temporarily added old events
        remove(this.events, event => this.flags.has(event._id));
      }

      this.events.unshift(...flaggedEvents);
    }

    this.loading = false;
    this.changeDetector.markForCheck();
  }

  private async loadComments(entry: any): Promise<void> {
    const temp: any = await this.vivoService.getCommentsForTask(entry._id);
    if (temp.docs.length > 0) {
      this.getCommentsSubject(entry).next(temp.docs);
    }
  }

  public async preloadComments(entry: any): Promise<void> {
    const subject = this.comments.get(entry._id);
    if (entry.messageCount && (!subject || subject.getValue().length !== entry.messageCount)) {
      // ensure subject exists before XHR has completed, to prevent duplicate preloads
      this.getCommentsSubject(entry);
      await this.loadComments(entry);
    }
  }

  public getCommentsSubject(entry: any): BehaviorSubject<any[]> {
    if (!this.comments.has(entry._id)) {
      this.comments.set(entry._id, new BehaviorSubject<any[]>(
        range(0, entry.messageCount || 0).map(i => ({
          _id: `placeholder-${entry._id}-${i}`,
          // texts are blurred so they're not readable: don't need to translate
          // the shape is the important part
          user: { username: 'Loading Name' },
          contentData: 'Loading content of this comment...',
          updatedAt: new Date(),
          placeholder: true
        }))
      ));
    }
    return this.comments.get(entry._id);
  }

  public async saveComment(id: string, message: string): Promise<void> {
    if (id && message) {
      const body = {
        eventData: {
          contentData: message,
          contentID: id,
          contentType: 'Task',
          type: 'newComment'
        }
      };
      this.commentText = null;
      this.loading = true;
      await this.vivoService.createComment(body);
    }
  }

  /**
   * setActive sets activeId which is used to indentify notification from list where mouse is on top of it
   * @param {Notification} type
   */
  public setActive(com: any) {
    this.commentText = '';
    if (this.activeId === com.id) {
      this.activeId = null;
    } else {
      this.loadComments(com);
      this.activeId = com.id;
    }
  }

  /**
   * isActive return true/false depending which notification mouse is hovering on
   * @param {Notification} type
   */
  public isActive(com: any) {
    if (com.id === this.activeId) {
      return true;
    }
    return false;
  }

  public getColor(level: number): string {
    switch (level) {
      case 1:
        return 'critical';
      case 2:
        return 'important';
      case 3:
        return 'green';
    }
  }

  public editMessage(s: any) {
    this.selectedEdit = s;
    this.edit = true;
  }

  public async toggleFlag(event: MouseEvent, id: string): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    this.loading = true;

    if (this.flags.has(id)) {
      await this.flagService.remove(this.flags.get(id));
    } else {
      const flag = new IFlag();
      flag.targetObjectId = id;
      flag.targetObjectType = 'diary';
      flag.global = false;
      flag.userId = this.authService.getSubject();

      await this.flagService.create(flag);
    }
  }

  public async saveEdit(edit: any): Promise<void> {
    this.loading = true;
    await this.vivoService.saveEdit(edit);
    this.edit = false;
    await this.prepareDiary();
  }

  public closeEdit() {
    this.selectedEdit = null;
    this.edit = false;
  }

  public async pageChanged(event: any): Promise<void> {
    if (this.pagination.page !== event) {
      this.vivoBody.page = event;
      this.loading = true;
      await this.prepareDiary();
    }
  }

  public trackById(index: number, element: any): string {
    return element._id || element.id || element.holderID || element.name;
  }

  public getNameOfUser(user?: { firstName?: string, lastName?: string, username?: string }): string {
    if (user?.firstName && user?.lastName) {
      return user.firstName + ' ' + user.lastName;
    }
    if (user?.username) {
      return user.username;
    }
    return this.translateService.instant(_('Default user'));
  }

  public getInitialsOfSubscriber(subscriber: { name?: string }): string {
    const parts = subscriber.name?.split(/[- ]/);
    if (!parts || parts.length === 0) {
      return '??';
    }

    // Include every name of multipart names, but use caps to filter out
    // single names that have dashes for grammar reasons.
    return parts.map(part => part[0]).filter(c => c.toUpperCase() === c).join('');
  }

  public getTaskLink(id: number): string {
    return 'https://miprovivo.trackinno.com/tasks/' + id;
  }

  /**
   * Returns a representation of the location chain for the given diary entry.
   * The widest area is listed first.
   */
  public getTaskLocations(task: any): TaskLocation[] {
    const out: TaskLocation[] = [];
    let item = task?.parents?.[0];
    while (item) {
      const id: string = item._id || item.id || item.holderID || item.name;

      // VIVO represents "unknown" using an item with ID "0"
      if (!id || id === '0') {
        break;
      }

      let link: string;
      let coordinates: MapLatLng;
      if (item.type === 'POI') {
        if (item.geo?.coordinates) {
          coordinates = {
            lat: item.geo?.coordinates[1],
            lng: item.geo?.coordinates[0],
          };
        }
      } else if (item.type === 'Asset') {
        link = 'https://miprovivo.trackinno.com/assets/' + id;
      } else {
        link = 'https://miprovivo.trackinno.com/organizationNodes/' + id;
      }

      // prepend so that the "widest" location ends up at the start
      out.unshift({
        _id: id,
        label: item.name,
        link,
        coordinates,
      });
      item = item.asset?.holder || item.parent;
    }
    return out;
  }

  public getItemStatusText(key: string): string {
    return ({
      pending: _('pending'),
      completed: _('completed'),
      late: _('late')
    })[key] || _('unknown');
  }

  public isSameDay(d1: string, d2: string): boolean {
    return isSameDay(parseJSON(d1), parseJSON(d2));
  }

  public onAddressClick(event: MouseEvent, coordinates: MapLatLng): void {
    event.preventDefault();
    event.stopPropagation();
    this.mapService.flyTo(coordinates, 14);
  }

  public togglePush(): void {
    this.stateService.mutatePartOfState('settingsState', (state) => {
      state.pushDiary = !state.pushDiary;
      if (state.pushDiary) {
        this.push.requestPermission().pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
          this.changeDetector.markForCheck();
        });
      }
    });
  }

  public isPushEnabled(): boolean {
    return this.stateService.getPartOfState('settingsState').pushDiary ?? false;
  }

  public isPushFailed(): boolean {
    return this.isPushEnabled() && !this.push.hasPermission();
  }

  public getPushToggleTitle(): string {
    if (this.isPushFailed()) {
      return this.translateService.instant(_("Notifications unavailable! Your browser doesn't allow REGO to show desktop notifications."));
    }
    return this.translateService.instant(_("Shows notifications about new entries on your desktop when REGO is minimized or otherwise not visible."));
  }
}
